import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, IdCard, Loader2, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errMsg, formatDate } from "../../lib/api";

const Row = ({ label, value }) => (
  <div><p className="text-[8px] font-semibold uppercase tracking-widest text-slate-400">{label}</p><p className="text-[12px] font-semibold leading-tight text-navy">{value || "-"}</p></div>
);

const Card = ({ data, cardRef }) => (
  <div ref={cardRef} className="w-[540px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl" data-testid="member-card">
    <div className="flex items-center gap-3 bg-navy px-5 py-3 text-white">
      <img src="/images/logo.jpg" alt="" className="h-12 w-12 rounded-full bg-white object-contain p-0.5" />
      <div className="flex-1">
        <p className="font-display text-[15px] font-extrabold tracking-tight">IT EMPLOYEES FEDERATION</p>
        <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-emerald-300">Voice. Rights. Welfare.</p>
      </div>
      <span className="shrink-0 whitespace-nowrap rounded bg-leaf px-2 py-1 text-[9px] font-bold uppercase tracking-widest">Member ID</span>
    </div>
    <div className="flex gap-5 px-5 py-4">
      <img src={data.photo} alt={data.full_name} className="h-[132px] w-[106px] rounded-lg border-2 border-royal object-cover" />
      <div className="grid flex-1 grid-cols-2 content-start gap-x-4 gap-y-2.5">
        <div className="col-span-2"><p className="font-display text-lg font-extrabold leading-tight text-navy" data-testid="member-card-name">{data.full_name}</p></div>
        <div className="col-span-2"><p className="text-[8px] font-semibold uppercase tracking-widest text-slate-400">Membership No.</p><p className="font-mono text-[15px] font-bold text-royal" data-testid="member-card-number">{data.member_no}</p></div>
        <Row label="Designation" value={data.designation} />
        <Row label="Employer" value={data.employer} />
        <Row label="District" value={data.district} />
        <Row label="Approved on" value={formatDate(data.approved_at || data.updated_at)} />
      </div>
    </div>
    <div className="flex items-center justify-between gap-3 whitespace-nowrap border-t-4 border-leaf bg-slate-50 px-5 py-2 text-[9px] text-slate-500">
      <span>{data.membership_category}</span>
      <span>Affiliated to STU – Swatantra Thozhilali Union</span>
    </div>
  </div>
);

export const MemberCardButton = ({ refId, mobile }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const open = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/applications/card", { ref_id: refId, mobile });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.href = url; a.download = `ITEF-Member-Card-${data.member_no}.png`; a.click();
    } catch (e) { toast.error("Could not create the image. Please try Print instead."); }
  };

  return (
    <>
      <button onClick={open} disabled={busy} className="btn-green mt-4 w-full sm:w-auto" data-testid="member-card-open-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <IdCard className="h-4 w-4" />} Get Digital Member ID Card
      </button>
      <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
        <DialogContent className="max-w-[620px]" data-testid="member-card-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Your Digital Member ID Card</DialogTitle></DialogHeader>
          {data && (
            <>
              <div className="member-card-print h-[170px] overflow-hidden sm:h-auto">
                <div className="w-[540px] origin-top-left scale-[0.6] sm:scale-100"><Card data={data} cardRef={cardRef} /></div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button onClick={() => window.print()} className="btn-outline" data-testid="member-card-print-button"><Printer className="h-4 w-4" /> Print</button>
                <button onClick={download} className="btn-primary" data-testid="member-card-download-button"><Download className="h-4 w-4" /> Download Card (PNG)</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
